import { useCallback, useState } from 'react';
import { PasseriniAPI } from '../services/apiService';

interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
  onClick: () => void;
}

interface PaginationData {
  current_page: number;
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  prev_page_url: string | null;
  path: string;
  per_page: number;
  to: number;
  total: number;
  links: Omit<PaginationLink, 'onClick'>[];
}

interface UseFetchParams {
  isPagination?: boolean;
  initialData?: any[];
}

interface UseFetchReturn {
  fetchData: (url: string, isPaginate?: boolean) => Promise<void>;
  data: any[] | null;
  loading: boolean;
  error: Error | null;
  links?: PaginationLink[];
  isSuccess: boolean;
  isError: boolean;
  currentPage?: number;
  totalItems?: number;
  perPage?: number;
  lastPage?: number;
  from?: number;
  to?: number;
}

export const useFetch = ({
  isPagination: isPaginationProps,
  initialData = []
}: UseFetchParams): UseFetchReturn => {
  const [isPagination, setIsPagination] = useState(isPaginationProps);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [data, setData] = useState<any[] | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async (url: string, isPaginate: boolean = isPagination || false) => {
    setLoading(true);
    try {
      const response = await PasseriniAPI.get(url);
      if (!isPaginate) {
        setData(response.data);
      } else {
        const responseData = response.data as any;
        const { data, ...paginationValues } = responseData;
        setData(data);
        setPagination(paginationValues as PaginationData);
      }
      setError(null);
    } catch (err) {
      setData(null);
      setPagination(null);
      setError(err instanceof Error ? err : new Error('Error al intentar obtener datos'));
    } finally {
      setLoading(false);
    }
  }, [isPagination]);

  const nextPage = () => {
    if (pagination?.next_page_url) fetchData(pagination.next_page_url);
  };

  const prevPage = () => {
    if (pagination?.prev_page_url) fetchData(pagination.prev_page_url);
  };

  const links = pagination?.links?.map(l => ({
    ...l,
    onClick: () => l.url && fetchData(l.url)
  })) || [];

  const changePage = (pageNumber: number) => {
    if (links && links[pageNumber]?.url) {
      fetchData(links[pageNumber].url!);
    } else if (pagination?.first_page_url) {
      fetchData(pagination.first_page_url);
    }
  };

  return {
    fetchData,
    data,
    loading,
    error,
    links,
    isSuccess: !error,
    isError: !!error,
    currentPage: pagination?.current_page,
    totalItems: pagination?.total,
    perPage: pagination?.per_page,
    lastPage: pagination?.last_page,
    from: pagination?.from,
    to: pagination?.to
  };
};
